import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import {
  ArrowRight, Anchor, Sun, Waves, Compass,
  Server, Network, Shield, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

/**
 * Greece / Thessaloniki — "Mediterranean data port" page.
 *
 * Visual identity intentionally different from every other location page:
 * - Centered hero (no split, no card-grid)
 * - Vertical "journey" stepper instead of feature cards — packets sailing
 *   the route to the user, like a shipping lane
 * - Palette: Aegean blue → ivory → sunset gold → olive
 * - Heavy use of horizontal rules and spacious typography to evoke
 *   a Mediterranean print magazine
 *
 * All page text inline as { en, ru }.
 */

const COPY = {
  en: {
    eyebrow: 'A new port on the Mediterranean map',
    h1: 'VPS hosting in Greece',
    h1Sub: 'From Thessaloniki, on the shores of the Aegean',
    lead: 'Greece has been a hub between three continents for three thousand years. Now it is a hub for your VPS too — in Thessaloniki, the country\'s second city and its first internet exchange, served by submarine cables that come ashore a short drive away from our cabinet.',
    cta: 'Set sail in Thessaloniki',
    altCta: 'See pricing',
    journeyTitle: 'The journey of a packet',
    journeySub: 'From the moment the request leaves your customer\'s phone in Athens, Tel Aviv or Cairo to the moment our server answers — here is what actually happens.',
    journey: [
      { tag: 'T+0 ms',   title: 'Request leaves the device',    body: 'Browser opens a TCP connection to your domain. DNS already cached, so we go straight to the IP.' },
      { tag: 'T+4 ms',   title: 'Last mile to the regional ISP', body: 'A few hops inside the customer\'s mobile or fixed-line provider — typically 3-7 ms across Greece, Cyprus, Turkey and the Levant.' },
      { tag: 'T+9 ms',   title: 'Onto the GR-IX exchange',       body: 'Carrier-neutral fabric in Athens and Thessaloniki — the routing decision happens here, in microseconds, with hundreds of peers visible at once.' },
      { tag: 'T+11 ms',  title: 'Arrives at our cabinet',        body: 'Edge router inside our suite hands the packet to your VPS\'s vNIC. End of hop count.' },
      { tag: 'T+12 ms',  title: 'Your application replies',      body: 'NVMe-backed storage, a generous CPU slice, and the answer is on its way back along the same short path.' },
    ],
    sideBoxTitle: 'Why a southern Mediterranean node matters',
    sideBoxBody: 'If your audience touches Cyprus, Egypt, Israel, Lebanon or Turkey, every hop you can subtract from a Frankfurt round-trip is a real percent of bounce-rate saved. Thessaloniki is sometimes 35–60 ms closer than the typical "European default".',
    figures: [
      { v: '< 5 ms',  l: 'Local Athens latency' },
      { v: '< 25 ms', l: 'To the wider Levant' },
      { v: '99.9%',   l: 'Power & uptime SLA' },
      { v: '1 Gbps',  l: 'Per-VPS port speed', wiki: 'shared-1gbps-port-explained', wikiLabel: 'What does shared 1 Gbps mean?' },
    ],
    pricingTitle: 'Greek VPS — plans and prices',
    pricingSub: 'Same plans, same prices as the rest of our European fleet — Mediterranean latency does not cost extra.',
    finalH: 'VPS in Greece — done as it should be.',
    finalSub: 'Three continents at one cable landing. Plant your VPS in the middle.',
    finalCta: 'Deploy your Greek VPS',
  },
  ru: {
    eyebrow: 'Новый порт на средиземноморской карте',
    h1: 'VPS-хостинг в Греции',
    h1Sub: 'Из Салоник, на берегу Эгейского моря',
    lead: 'Греция была перекрёстком трёх континентов три тысячи лет. Теперь это и перекрёсток для вашего VPS — в Салониках, втором по величине городе страны и его первой точке обмена интернет-трафиком, к которой подходят подводные кабели буквально в нескольких километрах от нашей стойки.',
    cta: 'Отплыть из Салоник',
    altCta: 'К тарифам',
    journeyTitle: 'Путь одного пакета',
    journeySub: 'С момента, как запрос покидает телефон клиента в Афинах, Тель-Авиве или Каире — до момента, как наш сервер отвечает. Вот что происходит на самом деле.',
    journey: [
      { tag: 'T+0 мс',  title: 'Запрос покидает устройство',      body: 'Браузер открывает TCP-соединение к вашему домену. DNS уже в кеше, идём сразу к IP.' },
      { tag: 'T+4 мс',  title: 'Последняя миля до регионального ISP', body: 'Несколько хопов внутри мобильного или фиксированного провайдера клиента — обычно 3–7 мс по Греции, Кипру, Турции и Леванту.' },
      { tag: 'T+9 мс',  title: 'Выход в обмен GR-IX',              body: 'Carrier-neutral фабрика в Афинах и Салониках — решение о маршруте принимается здесь, за микросекунды, видны сотни пиров одновременно.' },
      { tag: 'T+11 мс', title: 'Приход в нашу стойку',             body: 'Edge-маршрутизатор передаёт пакет в vNIC вашего VPS. Хопы закончились.' },
      { tag: 'T+12 мс', title: 'Ваше приложение отвечает',         body: 'NVMe-хранилище, щедрая доля CPU — и ответ уже летит обратно тем же коротким путём.' },
    ],
    sideBoxTitle: 'Зачем нужен южно-средиземноморский узел',
    sideBoxBody: 'Если ваша аудитория хоть как-то связана с Кипром, Египтом, Израилем, Ливаном или Турцией — каждый сэкономленный хоп от франкфуртского round-trip — это реальные проценты сохранённого bounce-rate. Салоники иногда на 35–60 мс ближе, чем «европейский по умолчанию».',
    figures: [
      { v: '< 5 мс',  l: 'Локальная задержка Афины' },
      { v: '< 25 мс', l: 'До Леванта в среднем' },
      { v: '99.9%',   l: 'SLA питания и uptime' },
      { v: '1 Гбит/с', l: 'Порт на каждый VPS', wiki: 'shared-1gbps-port-explained', wikiLabel: 'Что значит общий 1 Гбит/с?' },
    ],
    pricingTitle: 'Греческий VPS — планы и цены',
    pricingSub: 'Те же планы, те же цены, что и для остальных европейских локаций — средиземноморская задержка не стоит дороже.',
    finalH: 'VPS в Греции — каким он и должен быть.',
    finalSub: 'Три континента в одной кабельной посадке. Поставьте свой VPS посередине.',
    finalCta: 'Развернуть греческий VPS',
  }
};

export const GreecePage = () => {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;
  const journeyRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setRevealed(true); },
      { threshold: 0.2 }
    );
    if (journeyRef.current) obs.observe(journeyRef.current);
    return () => obs.disconnect();
  }, []);

  const scrollPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-[#fbfaf6]">
      <SEO page="greece" />
      <Header />

      {/* === HERO — full-bleed Aegean gradient, centered editorial === */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#073e6e] via-[#1a6da8] to-[#f4d27a]" />
        <div className="absolute inset-0 opacity-15"
             style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0 1px, transparent 2px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.35) 0 1px, transparent 2px)',
                       backgroundSize: '40px 40px, 60px 60px' }} />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 text-center text-white">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-50/90 mb-12">
            <Sun className="w-4 h-4" />
            <span>{c.eyebrow}</span>
            <span className="opacity-50">•</span>
          </div>

          <h1 className="font-serif font-black text-[3.5rem] sm:text-[4.5rem] lg:text-[6rem] leading-[0.95] tracking-tight">
            {c.h1}
          </h1>
          <p className="mt-6 text-lg sm:text-xl italic text-amber-50/95 font-light tracking-wide">
            {c.h1Sub}
          </p>

          <hr className="my-10 border-white/30 max-w-md mx-auto" />

          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            {c.lead}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={scrollPricing} className="bg-white text-[#073e6e] hover:bg-amber-50 font-bold px-9 py-6 text-lg rounded-full shadow-2xl">
              {c.cta} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <button onClick={scrollPricing} className="text-white/90 underline underline-offset-4 decoration-amber-200 hover:text-amber-100 text-sm">
              {c.altCta}
            </button>
          </div>
        </div>

        {/* Wave divider */}
        <svg className="block w-full h-16 text-[#fbfaf6]" viewBox="0 0 1200 60" preserveAspectRatio="none">
          <path d="M0 60 C 200 0 400 60 600 30 C 800 0 1000 60 1200 30 L1200 60 Z" fill="currentColor" />
        </svg>
      </section>


      {/* === IMAGE STRIP — Thessaloniki waterfront + Aegean === */}
      <section className="bg-[#fbfaf6] py-14 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="relative rounded-2xl overflow-hidden bg-[#1a6da8]/10 aspect-[4/3] lg:aspect-[5/3] shadow-lg">
              <img
                src="/images/locations/thessaloniki-white-tower.jpg"
                alt="White Tower of Thessaloniki — landmark on the Aegean waterfront"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                loading="lazy"
                width="1200" height="720"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative rounded-2xl overflow-hidden bg-[#1a6da8]/10 aspect-[4/3] lg:aspect-[5/3] shadow-lg">
              <img
                src="/images/locations/thessaloniki-waterfront.jpg"
                alt="Heptapyrgion fortress wall in Thessaloniki, Greece"
                loading="lazy"
                width="1200" height="720"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
      {/* === JOURNEY — vertical timeline stepper === */}
      <section ref={journeyRef} className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Anchor className="w-9 h-9 text-[#073e6e] mx-auto mb-4" />
            <h2 className="font-serif text-4xl sm:text-5xl font-black text-slate-900 mb-4">{c.journeyTitle}</h2>
            <p className="text-slate-600 text-lg leading-relaxed">{c.journeySub}</p>
          </div>

          <ol className="relative border-l-2 border-[#073e6e]/15 ml-6 lg:ml-12 space-y-12">
            {c.journey.map((step, i) => (
              <li key={i} className={`pl-10 transition-all duration-700 ${revealed ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                  style={{ transitionDelay: `${i * 130}ms` }}>
                <span className="absolute -left-[14px] mt-2 w-7 h-7 rounded-full bg-white border-2 border-[#073e6e] flex items-center justify-center shadow-sm">
                  <Waves className="w-3.5 h-3.5 text-[#073e6e]" />
                </span>
                <div className="text-xs uppercase tracking-widest text-amber-700 font-mono mb-1">{step.tag}</div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed max-w-2xl">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* === SIDE NOTE — quote box on amber background === */}
      <section className="py-20 bg-gradient-to-r from-[#fff6e0] to-[#fdedca] border-y border-amber-200/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-1 hidden lg:block">
            <div className="text-9xl font-serif text-[#073e6e]/30 leading-none -mt-6">"</div>
          </div>
          <div className="lg:col-span-11">
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-3">{c.sideBoxTitle}</h3>
            <p className="text-slate-700 text-lg leading-relaxed">{c.sideBoxBody}</p>
          </div>
        </div>
      </section>

      {/* === FIGURES — circular badges, not card grid === */}
      <section className="py-20 bg-[#fbfaf6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {c.figures.map((f, i) => (
            <div key={i} className="text-center">
              <div className="mx-auto w-32 h-32 rounded-full bg-white border-2 border-[#073e6e]/10 shadow-md flex items-center justify-center mb-4">
                <span className="text-2xl font-black text-[#073e6e]">{f.v}</span>
              </div>
              <div className="text-sm text-slate-500 uppercase tracking-wider">{f.l}</div>
              {f.wiki && (
                <Link
                  to={(language === 'ru' ? '/ru' : '') + '/wiki/' + f.wiki}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-[#073e6e] hover:text-[#0a5da5] transition-colors normal-case tracking-normal"
                >
                  {f.wikiLabel}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <RelatedLocations currentLocation="vps-greece" />

      <div id="pricing">
        <PricingSection hideHeader={false} title={c.pricingTitle} subtitle={c.pricingSub} />
      </div>
      <PaymentMethodsSection transparentBg={false} />

      {/* === FINAL CTA === */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#073e6e] to-[#1a6da8]" />
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: 'radial-gradient(circle at 50% 100%, rgba(244,210,122,0.5) 0, transparent 60%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Compass className="w-12 h-12 text-amber-200 mx-auto mb-6" />
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-3">{c.finalH}</h2>
          <p className="text-xl text-amber-50/90 italic mb-12">{c.finalSub}</p>
          <Button onClick={scrollPricing} className="bg-amber-300 hover:bg-amber-200 text-[#073e6e] font-bold px-10 py-7 text-lg rounded-full shadow-2xl">
            {c.finalCta} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-greece" />
      <Footer />
    </div>
  );
};
